import { type CurrencyIconsCodes, type FiatCurrency, SsuLogoPlaceholder, WithSkeleton } from '@capsicum/common';
import { type StaticImageData } from 'next/image';
import styled, { useTheme } from 'styled-components';

import { useCurrencyIcon } from '../../../hooks/helpers/useCurrencyIcon';
import { SkeletonItem, TextLineOrSkeleton } from '../../atoms';
import { Image } from '../../atoms/image/Image';
import { ChevronDownIcon } from '../../icons/ChevronDown';

import { ConversionRates } from './conversionRates/ConversionRates';

export type CurrencyDisplayProps = {
	altCurrencyAmount?: string;
	currencyList?: Array<FiatCurrency>;
	currencyLogo?: StaticImageData | string;
	currencyLogoBlurhash?: string;
	currencyShortName?: CurrencyIconsCodes | string;
	defaultCurrencyAmount?: string;
	disabled?: boolean;
	isConversionRatesLoading?: boolean;
	onClick?: () => void;
	partnerName?: string;
	showCurrencySelector?: boolean;
	testID?: string;
};

const PartneNameComponent = ({
	currencyShortName,
	disabled,
	partnerName
}: {
	currencyShortName?: string;
	disabled: boolean;
	partnerName?: string;
}) => (
	<S.TextWrapper>
		<S.PartnerName $disabled={disabled}>{currencyShortName}</S.PartnerName>
		<S.AmountText>{partnerName}</S.AmountText>
	</S.TextWrapper>
);

const PartnerNameSkeleton = () => (
	<S.TextWrapper>
		<SkeletonItem height={18} width={40} />
		<SkeletonItem height={18} width={80} />
	</S.TextWrapper>
);

const PartnerName = WithSkeleton(PartneNameComponent, PartnerNameSkeleton);

export const CurrencyDisplay = ({
	altCurrencyAmount,
	currencyList,
	currencyLogo,
	currencyLogoBlurhash,
	currencyShortName,
	defaultCurrencyAmount,
	disabled = false,
	isConversionRatesLoading = false,
	onClick,
	partnerName,
	showCurrencySelector = true,
	testID
}: CurrencyDisplayProps) => {
	const { colors } = useTheme();
	const { getCurrencyIcon } = useCurrencyIcon();

	const defaultCurrencyLogo = getCurrencyIcon(currencyShortName, 'big');

	const isCurrencyLogo = currencyLogo && !isConversionRatesLoading;

	return (
		<S.Wrapper $showCurrencySelector={showCurrencySelector} data-testid={testID}>
			<S.LogoWrapper>
				{isCurrencyLogo ? (
					<Image
						alt="currency-logo"
						blurhash={currencyLogoBlurhash}
						data-testid={`${testID}-logo`}
						fallbackSrc={SsuLogoPlaceholder}
						height={60}
						src={currencyLogo}
						width={60}
					/>
				) : (
					defaultCurrencyLogo
				)}
			</S.LogoWrapper>
			{showCurrencySelector ? (
				<S.TextWrapper data-testid={`${testID}-selector`}>
					<TextLineOrSkeleton isLoading={isConversionRatesLoading}>
						<S.PartnerName $disabled={disabled}>
							{currencyShortName}

							{((currencyList && currencyList?.length) || !!onClick) && (
								<S.CurrencySelector onClick={onClick}>
									<ChevronDownIcon color={colors.icons.info.enabled} height={25} />
								</S.CurrencySelector>
							)}
						</S.PartnerName>
					</TextLineOrSkeleton>
					<S.Amounts $disabled={disabled}>
						<ConversionRates
							altCurrencyAmount={altCurrencyAmount}
							defaultCurrencyAmount={defaultCurrencyAmount}
							isLoading={isConversionRatesLoading}
						/>
					</S.Amounts>
				</S.TextWrapper>
			) : (
				<PartnerName
					currencyShortName={currencyShortName}
					disabled={disabled}
					isLoading={isConversionRatesLoading}
					partnerName={partnerName}
				/>
			)}
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div<{ $showCurrencySelector?: boolean }>`
		display: grid;
		grid-template-columns: 60px auto 1fr;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		width: 100%;
		flex: 1;
		align-items: ${({ $showCurrencySelector }) => ($showCurrencySelector ? 'flex-start' : 'center')};
	`;

	export const LogoWrapper = styled.div``;

	export const TextWrapper = styled.div`
		display: flex;
		flex-direction: column;
	`;

	export const AmountText = styled.span`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const PartnerName = styled.div<{ $disabled: boolean }>`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ $disabled, theme }) =>
			$disabled ? theme.colors.text.main.disabled : theme.colors.text.main.enabled};
		display: flex;
	`;

	export const CurrencySelector = styled.div`
		margin-left: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Amounts = styled.div<{ $disabled: boolean }>`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ $disabled, theme }) =>
			$disabled ? theme.colors.text.main.disabled : theme.colors.backgrounds.status.participated};
	`;
}
